import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { t } from '@lingui/macro';
import { useQueryClient } from '@tanstack/react-query';

import { Box, Heading } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import BackButtonWithFormState from '@rover/react-lib/src/pages/provider-profile/components/BackButtonWithFormState';
import { useApiProviderProfileModuleCreate } from '@rover/rsdk/src/apiClient/latest/api-provider-profile-module-create';
import {
  getApiProviderProfileModuleRetrieveQueryKey,
  useApiProviderProfileModuleRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-provider-profile-module-retrieve';
import type { ExperienceModule } from '@rover/rsdk/src/apiClient/latest/model/experienceModule';
import type { ExperienceModuleWhatToExpectPartial } from '@rover/rsdk/src/apiClient/latest/model/experienceModuleWhatToExpectPartial';
import type { ExperiencePersonConfig } from '@rover/rsdk/src/apiClient/latest/model/experiencePersonConfig';
import { HttpError } from '@rover/rsdk/src/errors';
import { Logger } from '@rover/rsdk/src/modules/Logging';

import { MODULE_KEYS } from '../constants/moduleKeys';
import useBackRouteParams from '../SitterProfilePage/hooks/useBackRouteParams';
import { ReturnQueryParam } from '../types/ServiceForm';

import type { WhatToExpectFormData } from './components';
import { WhatToExpectForm, WhatToExpectFormShimmer } from './components';

const WhatToExpectPage: React.FC = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [showBackModal, setShowBackModal] = useState(false);
  const [submissionErrors, setSubmissionErrors] = useState<Record<string, string[] | string>>({});

  const { backRoute } = useBackRouteParams(ReturnQueryParam.ExperienceAdded);

  // fetch Experience module data
  const { data: experienceData, isLoading: experienceIsLoading } =
    useApiProviderProfileModuleRetrieve(MODULE_KEYS.EXPERIENCE);

  const { mutate, isLoading: isMutating } = useApiProviderProfileModuleCreate({
    mutation: {
      onSuccess: async (response) => {
        setSubmissionErrors({});
        // Also update the experience endpoint cache with the new data
        queryClient.setQueryData(
          getApiProviderProfileModuleRetrieveQueryKey(MODULE_KEYS.EXPERIENCE),
          {
            ...experienceData,
            data: {
              ...experienceData?.data,
              ...response.data,
            },
          }
        );
        // Use React Router navigation to preserve cache state
        history.push(backRoute);
      },
      onError: (error) => {
        Logger.info('What to expect form error', {
          errorMessage: error.message,
        });

        if (error instanceof HttpError && error.rawResponse?.data) {
          setSubmissionErrors(error.rawResponse.data as Record<string, string[] | string>);
        }
      },
    },
  });

  const experienceModule = experienceData?.data as ExperienceModule | undefined;
  const config = experienceData?.config as ExperiencePersonConfig | undefined;
  const minWordCount = config?.minWordCount ?? 0;
  const isSignUp = config?.isSignUp ?? false;

  // Partial experienceModule data as WhatToExpectFormData
  const initialFormData: WhatToExpectFormData = {
    schedule: experienceModule?.availability || '',
    environment: experienceModule?.environment || '',
    experience: experienceModule?.dogsExperience || '',
    info: experienceModule?.requestedDogInfo || '',
  };

  const handleSubmit = (formData: WhatToExpectFormData): void => {
    // Prepare partial data for ExperienceModuleWhatToExpect
    const data: ExperienceModuleWhatToExpectPartial = {
      availability: formData.schedule,
      environment: formData.environment,
      dogsExperience: formData.experience,
      requestedDogInfo: formData.info,
    };

    // Partial update ExperienceModule via ExperienceWhatToExpectModule endpoint
    mutate({
      moduleSlug: MODULE_KEYS.EXPERIENCE_WHAT_TO_EXPECT,
      data,
    });
  };

  return (
    <>
      <Helmet>
        <title>{t`What to expect`}</title>
      </Helmet>

      <Section maxWidth={['100%', '640px']} data-qa-id="what-to-expect-page" py="6x">
        <BackButtonWithFormState href={backRoute} skipModal={!showBackModal} />

        <Box py="8x">
          <Heading size="400" mb="8x">
            {t`What to expect`}
          </Heading>

          {experienceIsLoading ? (
            <WhatToExpectFormShimmer />
          ) : (
            <WhatToExpectForm
              initialData={initialFormData}
              isSubmitting={isMutating}
              onSubmit={handleSubmit}
              minWordCount={minWordCount}
              setShowBackModal={setShowBackModal}
              submissionErrors={submissionErrors}
              isSignUp={isSignUp}
            />
          )}
        </Box>
      </Section>
    </>
  );
};

export default WhatToExpectPage;
